/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { consult } from 'store/modules/juridico_facil_store/actions';
import { JuridicoFacilState } from 'store/modules/juridico_facil_store/types';
import moment from 'moment';
import AiSuggestion from 'components/organisms/ai_suggestion';
import { Link, useLocation } from 'react-router-dom';
import OpenTutorial from 'components/atoms/open_tutorial';
import AiSuggestionFloating from 'components/organisms/ai_suggestion_floating';
import { Container } from './styles';

interface BiddingMarketProps {
  params: {
    id?: string;
  };
}

const scrollToTopSmart = () => {
  const containers = Array.from(document.querySelectorAll<HTMLElement>('*'));
  const scrollable = containers.find((el) => {
    const style = window.getComputedStyle(el);
    const { overflowY } = style;
    const canScroll =
      (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
    return canScroll;
  });

  if (scrollable) {
    scrollable.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

// Labels das abas vêm crus do JusBrasil, sem acento. O conjunto é aberto — um
// tribunal novo pode trazer um label ainda não visto —, então os conhecidos têm
// tradução e o resto cai numa humanização genérica.
const ROTULOS_CONHECIDOS: Record<string, string> = {
  INTEIRO_TEOR: 'Inteiro Teor',
  RELATORIO_VOTO: 'Relatório e Voto',
  CERTIDAO_JULGAMENTO: 'Certidão de Julgamento',
  FATOS: 'Fatos',
  FATOS_SIMPLES: 'Fatos Simplificados',
  EMENTA: 'Ementa',
  ACORDAO: 'Acórdão',
  DECISAO: 'Decisão',
};

const humanizarLabel = (label?: string | null) =>
  ROTULOS_CONHECIDOS[(label || '').toUpperCase()] ||
  (label || 'Texto')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (letra) => letra.toUpperCase());

const JuridicoFacilShow = (props: BiddingMarketProps) => {
  const dispatch = useDispatch();
  const [alert, setAlert] = useState<boolean>(false);
  const [activeAnchor, setActiveAnchor] = useState<string>();
  const [abaAtiva, setAbaAtiva] = useState<number>(0);

  const { params } = props;
  const { juridico } = useSelector<ApplicationState, JuridicoFacilState>(
    (state) => state.session.juridico,
  );
  const textToCopyRef = useRef(null);

  // O id sozinho é ambíguo: as faixas de id do acervo curado e do extrator
  // JusBrasil se sobrepõem. A origem vem da query string do link de origem.
  const { search } = useLocation();
  const fonte = new URLSearchParams(search).get('fonte') || undefined;

  const item = juridico?.conteudo?.[0];
  const doJusBrasil = item?.fonte === 'jusbrasil';

  useEffect(() => {
    if (!params?.id) return;
    dispatch(consult(String(params.id), fonte));
  }, [params?.id, fonte]);

  useEffect(() => {
    setAbaAtiva(0);
  }, [juridico?.textos]);

  useEffect(() => {
    if (!juridico?.conteudo?.length) return;

    requestAnimationFrame(() => {
      scrollToTopSmart();
    });
  }, [juridico?.conteudo]);

  const handleCopyClick = () => {
    if (textToCopyRef.current) {
      const range = document.createRange();
      range.selectNode(textToCopyRef.current);

      const selection = window.getSelection();

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);

        try {
          document.execCommand('copy');
          setAlert(true);
          selection.removeAllRanges();
        } catch (err) {
          console.error('Falha ao copiar texto para a área de transferência:', err);
        }
      }
    }
  };

  const handleAnchorClick = (anchor: string, event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setActiveAnchor(anchor);
  };

  return (
    <Container>
      <div className="mt-5 py-2">
        {alert && (
          <div className="alert alert-success text-center" role="alert">
            <strong>Copiado com sucesso!!</strong> Utilize o atalho ctrl+v para colar em seu
            documento.
          </div>
        )}
        <div className="container p-4 mt-2">
          <div className="row">
            <div className="col-md-12">
              <div className="d-md-flex d-block align-items-center mb-3">
                <h4 className="mb-1 mr-3 font-weight-bold">Jurídico Fácil</h4>
                <OpenTutorial type="modal" source="juridico" />
              </div>
            </div>
            <div className="mb-3">
              <Link to="/juridico">
                <i className="material-icons align-middle">keyboard_arrow_left</i> Voltar aos
                Resultados
              </Link>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-3 mb-3">
              <div className="card sticky-top">
                <div className="card-header">Destacar no texto</div>
                <ul id="link-fixo" className="list-group list-group-flush">
                  {juridico?.conteudo[0]?.acordao !== '' && (
                    <li className="list-group-item">
                      <a href="#acordao" onClick={(e) => handleAnchorClick('acordao', e)}>
                        Acordão
                      </a>
                    </li>
                  )}
                  {juridico?.conteudo[0]?.autor_da_tese !== '' && (
                    <li className="list-group-item">
                      <a href="#relator" onClick={(e) => handleAnchorClick('relator', e)}>
                        Relator
                      </a>
                    </li>
                  )}
                  {juridico?.conteudo[0]?.enunciado !== '' && (
                    <li className="list-group-item">
                      <a href="#enunciado" onClick={(e) => handleAnchorClick('enunciado', e)}>
                        Enunciado
                      </a>
                    </li>
                  )}
                  {!!juridico?.conteudo[0]?.conteudo && (
                    <li className="list-group-item">
                      <a href="#excerto" onClick={(e) => handleAnchorClick('excerto', e)}>
                        Excerto
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            </div>
            <div className="col-md-9">
              <div className="card tour-easy-legal-card">
                <div className="card-body">
                  <h4 className="font-weight-bold">{juridico?.conteudo[0]?.tipo}</h4>
                  {juridico?.conteudo[0]?.dica_em_video && (
                    <div className="col-sm py-3">
                      <div className="video-container">
                        <iframe
                          id="player"
                          width="720"
                          height="405"
                          src={`https://www.youtube-nocookie.com/embed/${juridico?.conteudo[0]?.dica_em_video.replace(
                            /https:\/\/youtu.be\//,
                            '',
                          )}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}
                  {juridico?.conteudo[0]?.tipo !== 'Dica Legal' && (
                    <>
                      <div className="row">
                        <div
                          id="acordao"
                          className={`col-sm ${
                            activeAnchor === 'acordao' ? 'alert alert-primary' : ''
                          }`}
                        >
                          {juridico?.conteudo[0]?.tipo === 'Jurisprudência'
                            ? `Acórdão: ${juridico?.conteudo[0]?.acordao}`
                            : juridico?.conteudo[0]?.acordao}
                        </div>
                        <div
                          id="data_sessao"
                          className={`col-sm ${
                            activeAnchor === 'data_sessao' ? 'alert alert-primary' : ''
                          }`}
                        >
                          {juridico?.conteudo[0]?.data_sessao !== '' &&
                            `Data da sessão: ${moment(juridico?.conteudo[0]?.data_sessao).format(
                              'l',
                            )}`}
                        </div>
                      </div>
                      <div className="row">
                        <div
                          id="relator"
                          className={`col-sm ${
                            activeAnchor === 'relator' ? 'alert alert-primary' : ''
                          }`}
                        >
                          {juridico?.conteudo[0]?.autor_da_tese}
                        </div>
                        <div className="col-sm">
                          {juridico?.conteudo[0]?.tipo_de_processo !== '' &&
                            `Tipo de processo ${juridico?.conteudo[0]?.tipo_de_processo}`}
                        </div>
                      </div>
                    </>
                  )}
                  {doJusBrasil && (
                    <div className="row mt-2">
                      {!!item?.court && <div className="col-sm">{`Tribunal: ${item?.court}`}</div>}
                      {!!item?.grau && <div className="col-sm">{`Grau: ${item?.grau}`}</div>}
                      {!!item?.url && (
                        <div className="col-sm">
                          <a href={item?.url} target="_blank" rel="noopener noreferrer">
                            Ver no JusBrasil
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="row mt-3">
                    <div className="col-sm-12">
                      <nav id="idx_breadcrumb">
                        <ol className="breadcrumb breadcrumb-custom">
                          <li className="breadcrumb-item">
                            <a href="#" data-abc="true">
                              <strong>{juridico?.conteudo[0]?.tipo}</strong>
                            </a>
                          </li>
                          <li className="breadcrumb-item">
                            <a href="#" data-abc="true">
                              {juridico?.conteudo[0]?.area}
                            </a>
                          </li>
                          {!!item?.tema && (
                            <li className="breadcrumb-item">
                              <a href="#" data-abc="true">
                                {item?.tema}
                              </a>
                            </li>
                          )}
                          {!!item?.subtema && (
                            <li className="breadcrumb-item">
                              <a href="#" data-abc="true">
                                {item?.subtema}
                              </a>
                            </li>
                          )}
                        </ol>
                      </nav>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm">
                      {!!juridico?.conteudo[0]?.tags && (
                        <>
                          <span className="font-weight-bold">Outros indexadores:</span>
                          <b> </b>
                          <i>{juridico?.conteudo[0]?.tags}</i>
                        </>
                      )}
                    </div>
                  </div>
                  {juridico?.conteudo[0]?.enunciado !== '' && (
                    <div className="row my-4">
                      <div
                        id="enunciado"
                        className={`col-sm text-justify ${
                          activeAnchor === 'enunciado' ? 'alert alert-primary' : ''
                        }`}
                        ref={textToCopyRef}
                      >
                        <h6 className="font-weight-bold">Enunciado</h6>
                        <span
                          dangerouslySetInnerHTML={{ __html: juridico?.conteudo[0]?.enunciado }}
                        />

                        {juridico?.conteudo[0]?.tipo !== 'Dica Legal' && (
                          <button
                            type="button"
                            onClick={() => handleCopyClick()}
                            className="badge badge-danger btn btn-outline-danger"
                          >
                            Copiar para Citação
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {!!juridico?.conteudo[0]?.conteudo && (
                    <div className="row mb-4">
                      <div
                        id="excerto"
                        className={`col-sm text-justify ${
                          activeAnchor === 'excerto' ? 'alert alert-primary' : ''
                        }`}
                      >
                        <h6 className="font-weight-bold">Excerto</h6>
                        <span
                          style={doJusBrasil ? { whiteSpace: 'pre-wrap' } : undefined}
                          dangerouslySetInnerHTML={{ __html: juridico?.conteudo?.[0]?.conteudo }}
                        />
                      </div>
                    </div>
                  )}
                  {juridico?.textos?.length > 0 && (
                    <div className="row mb-4">
                      <div id="textos" className="col-sm text-justify">
                        <h6 className="font-weight-bold">Texto integral</h6>
                        <ul className="nav nav-tabs" role="tablist">
                          {juridico?.textos?.map((texto, indice) => (
                            <li className="nav-item" key={texto?.id}>
                              <button
                                type="button"
                                className={`nav-link btn btn-link ${
                                  indice === abaAtiva ? 'active' : ''
                                }`}
                                onClick={() => setAbaAtiva(indice)}
                              >
                                {humanizarLabel(texto?.label)}
                              </button>
                            </li>
                          ))}
                        </ul>
                        {/* Sem borda, sem altura fixa e sem rolagem própria: o
                            acórdão tem ~20 mil caracteres e quem rola é a página. */}
                        <div className="pt-3" style={{ whiteSpace: 'pre-wrap' }}>
                          {juridico?.textos?.[abaAtiva]?.conteudo}
                        </div>
                      </div>
                    </div>
                  )}
                  {juridico?.referencia_legal?.[0]?.referencia_legal && (
                    <div className="row mb-4">
                      <div id="referencia_legal" className="col-sm">
                        <span className="d-block mb-2">
                          <b>Referência Legal</b>
                        </span>

                        {juridico?.referencia_legal?.map((ref_legal) => (
                          <span className="d-block">{ref_legal?.referencia_legal}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <AiSuggestionFloating sugestoes={juridico?.sugestoes} />
              <AiSuggestion sugestoes={juridico?.sugestoes} />
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default JuridicoFacilShow;
